/**
 * Workflow Executions — per-workflow run history & step logs.
 *
 * Mirrors the Campaign Detail experience: a workflow's execution history lands
 * here, and drilling into a single run reveals its full step-by-step timeline
 * (delay → send campaign → …) with the REAL campaign delivery outcome joined in.
 *
 * Presentation is deliberately object-id-free: leads are shown by name/phone
 * (from the captured lead snapshot), campaigns by name, and node steps by a
 * friendly label — never a raw Mongo id.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, IconButton, Alert,
  CircularProgress, Stack, Tooltip, Chip, Divider,
} from '@mui/material';
import {
  ArrowBack, Refresh, ChevronRight, Edit, Campaign as CampaignIcon,
  AccessTime, Person,
} from '@mui/icons-material';
import adminApi from '@/services/admin.api';
import { field, useUrlListState } from '@/hooks/admin/useUrlListState';

/** Server-side page size for both run tables on this screen. */
const PAGE_SIZE = 20;

// ─── Types ──────────────────────────────────────────────────────────────────

interface LeadSnapshot {
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  source?: string;
  status?: string;
}

interface ExecutionLogEntry {
  nodeId: string;
  nodeType: string;
  status: 'success' | 'failure' | 'skipped';
  output?: any;
  error?: string;
  startedAt: string;
  completedAt: string;
}

interface Execution {
  _id: string;
  workflowId: string;
  leadId: string;
  status: string;
  retryCount: number;
  startedAt: string;
  completedAt?: string;
  failedAt?: string;
  context?: { lead?: LeadSnapshot };
  executionLog: ExecutionLogEntry[];
}

interface Workflow {
  _id: string;
  name: string;
  description?: string;
  trigger?: { type: string };
}

/** A single message.received run — a separate, much smaller shape from
 *  Execution above (no lead, no resumability; see conversation-flow-run-log.
 *  service.ts on the backend for why). */
interface ConversationFlowRunActionSummary {
  type:
    | 'human_handoff'
    | 'canned_reply'
    | 'template_reply'
    | 'send_media'
    | 'send_list_message'
    | 'send_button_message';
  replyPreview?: string;
  templateName?: string;
  mediaAssetId?: string;
  listButtonText?: string;
  buttonCount?: number;
}

interface ConversationFlowRun {
  _id: string;
  conversationId: string;
  matchedNodeId: string;
  matchedPhrase?: string;
  matchedButtonId?: string;
  actions: ConversationFlowRunActionSummary[];
  customerPhone: string;
  customerName?: string;
  createdAt: string;
}

interface CampaignDeliveryLog {
  contactPhone: string;
  contactName?: string;
  status: string;
  templateName?: string;
  attempts?: number;
  error?: string;
  providerError?: { code?: number; title?: string; message?: string; details?: string };
  sentAt?: string;
  deliveredAt?: string;
  failedAt?: string;
}

interface CampaignActivity {
  runId: string;
  nodeId?: string;
  campaignId: string;
  campaignName?: string;
  triggerEvent: string;
  status: string;
  failureReason?: string;
  totals: { targeted: number; sent: number; delivered: number; failed: number; skipped: number };
  startedAt: string;
  completedAt?: string;
  logs: CampaignDeliveryLog[];
}

// ─── Constants ──────────────────────────────────────────────────────────────

const TRIGGER_LABELS: Record<string, string> = {
  'lead.created':        'Incoming lead',
  'lead.updated':        'Lead updated',
  'lead.status_changed': 'Lead status changed',
  'lead.priority_changed': 'Lead priority changed',
  'appointment.created':        'Incoming appointment',
  'appointment.status_changed': 'Appointment status changed',
  'campaign.sent':       'Campaign sent',
  'campaign.failed':     'Campaign failed',
  'campaign.replied':    'Campaign replied',
  'manual':              'Manual / API',
};

const NODE_LABELS: Record<string, string> = {
  trigger:          'Trigger',
  delay:            'Delay',
  send_campaign:    'Send campaign',
  assign_agent:     'Assign agent',
  condition:        'Condition',
  update_lead:      'Update lead',
  webhook:          'Webhook',
  trigger_workflow: 'Trigger workflow',
};

// Execution status → dot color.
const EXEC_STATUS_COLOR: Record<string, string> = {
  completed:     'success',
  running:       'info',
  waiting_delay: 'warning',
  pending:       'default',
  failed:        'error',
  cancelled:     'default',
};

const EXEC_STATUS_LABEL: Record<string, string> = {
  completed:     'Completed',
  running:       'Running',
  waiting_delay: 'Waiting (delay)',
  pending:       'Pending',
  failed:        'Failed',
  cancelled:     'Cancelled',
};

const STEP_STATUS_COLOR: Record<string, string> = {
  success: 'success',
  failure: 'error',
  skipped: 'default',
};

const DELIVERY_STATUS_COLOR: Record<string, string> = {
  delivered: 'success',
  sent:      'info',
  pending:   'default',
  failed:    'error',
  optOut:    'warning',
  skipped:   'default',
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function fmtTime(value?: string): string {
  return value ? new Date(value).toLocaleString() : '—';
}

function fmtDuration(start?: string, end?: string): string {
  if (!start || !end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 0) return '—';
  if (ms < 1000) return '<1s';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

/** Configured delay as a natural phrase, e.g. "1 minute", "5 minutes". */
function formatDelay(amount?: number, unit?: string): string | null {
  if (amount == null || !unit) return null;
  const singular = unit.replace(/s$/, '');
  return `${amount} ${amount === 1 ? singular : `${singular}s`}`;
}

/**
 * The REAL time a step occupied in the run.
 * A delay node executes instantly (it only *schedules* the resume), so its own
 * startedAt→completedAt span is ~0. The truthful wait is the wall-clock gap until
 * the next step actually started; if it's the last step (still waiting / run ended
 * on it), fall back to the configured resume time or the configured delay.
 */
function stepDuration(entry: ExecutionLogEntry, next?: ExecutionLogEntry): string {
  if (entry.nodeType === 'delay') {
    if (next?.startedAt) return fmtDuration(entry.startedAt, next.startedAt);
    const o = entry.output ?? {};
    if (o.resumeAt) return fmtDuration(entry.startedAt, o.resumeAt);
    const configured = formatDelay(o.amount, o.unit);
    if (configured) return configured;
    return '—';
  }
  return fmtDuration(entry.startedAt, entry.completedAt);
}

function leadLabel(lead?: LeadSnapshot): { primary: string; secondary?: string } {
  if (!lead) return { primary: 'Unknown lead' };
  const name = lead.customerName?.trim();
  const phone = lead.customerPhone?.trim();
  if (name && phone) return { primary: name, secondary: phone };
  if (name) return { primary: name };
  if (phone) return { primary: phone };
  if (lead.customerEmail) return { primary: lead.customerEmail };
  return { primary: 'Unknown lead' };
}

/**
 * Human-readable, id-free summary of a node step's output. Deliberately drops
 * any Mongo id (campaignId, nodeId, assignedTo, *Id keys, 24-hex values) so no
 * object id ever reaches the screen. send_campaign is handled separately via
 * the delivery panel and never routed through here.
 */
function friendlyOutput(entry: ExecutionLogEntry): string | null {
  const o = entry.output;
  if (!o || typeof o !== 'object') return null;

  const parts: string[] = [];
  if (o.reason) parts.push(String(o.reason));
  if (o.statusCode != null) parts.push(`HTTP ${o.statusCode}`);
  if (o.assignedTo) parts.push('Agent assigned');
  if (o.triggeredWorkflowId) parts.push('Started a linked workflow');
  if (o.result != null && typeof o.result !== 'object') parts.push(`Result: ${o.result}`);
  if (o.branch) parts.push(`Branch: ${o.branch}`);

  // Generic field updates (e.g. update_lead) — skip id-ish keys and 24-hex ids.
  const HEX24 = /^[a-f0-9]{24}$/i;
  for (const [k, v] of Object.entries(o)) {
    if (['queued', 'skipped', 'reason', 'statusCode', 'assignedTo', 'triggeredWorkflowId',
         'result', 'branch', 'campaignId', 'nodeId', 'body'].includes(k)) continue;
    if (/id$/i.test(k) || k === '_id') continue;
    if (v == null || typeof v === 'object') continue;
    if (typeof v === 'string' && HEX24.test(v)) continue;
    parts.push(`${k}: ${v}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

/** Meta-style dot + text status indicator (matches Campaign Detail). */
function StatusDot({ color, label }: { color: string; label: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{
          width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
          bgcolor: color === 'default' ? 'grey.400' : `${color}.main`,
        }}
      />
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Stack>
  );
}

// ─── Campaign delivery panel ──────────────────────────────────────────────────
// The REAL outcome of a send_campaign step: the linked CampaignRun's totals plus
// each recipient's true sent/delivered/failed status. Includes a redirect to the
// full campaign logs page.

function CampaignDeliveryPanel({
  act, onOpenCampaign,
}: { act: CampaignActivity; onOpenCampaign: (campaignId: string) => void }) {
  const t = act.totals ?? { targeted: 0, sent: 0, delivered: 0, failed: 0, skipped: 0 };
  return (
    <Box sx={{ mt: 1, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap mb={1}>
        <CampaignIcon fontSize="small" color="action" />
        <Typography variant="body2" fontWeight={600}>
          {act.campaignName ?? 'Campaign'}
        </Typography>
        <StatusDot
          color={EXEC_STATUS_COLOR[act.status] ?? 'default'}
          label={act.status.charAt(0).toUpperCase() + act.status.slice(1)}
        />
        <Box flexGrow={1} />
        <Button
          size="small"
          startIcon={<CampaignIcon fontSize="small" />}
          onClick={() => onOpenCampaign(act.campaignId)}
        >
          View campaign logs
        </Button>
      </Stack>

      {/* Aggregate totals as quiet inline stats */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: act.failureReason ? 1 : 0 }}>
        {(['targeted', 'sent', 'delivered', 'failed', 'skipped'] as const).map((k) => (
          <Typography key={k} variant="caption" color="text.secondary">
            {k.charAt(0).toUpperCase() + k.slice(1)}:{' '}
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                color:
                  k === 'failed' && (t.failed ?? 0) > 0 ? 'error.main'
                  : k === 'delivered' && (t.delivered ?? 0) > 0 ? 'success.main'
                  : 'text.primary',
              }}
            >
              {t[k] ?? 0}
            </Box>
          </Typography>
        ))}
      </Stack>

      {act.failureReason && (
        <Typography variant="caption" color="error.main" display="block">
          {act.failureReason}
        </Typography>
      )}

      {/* Per-recipient rows */}
      {act.logs.length === 0 ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          No per-recipient records — the recipient was deduplicated (already messaged
          within the window) or the run had no targets.
        </Typography>
      ) : (
        <>
          <Divider sx={{ my: 1 }} />
          <Stack spacing={1}>
            {act.logs.map((log, i) => (
              <Box key={i}>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <StatusDot
                    color={DELIVERY_STATUS_COLOR[log.status] ?? 'default'}
                    label={log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                  />
                  <Typography variant="caption" fontWeight={600}>
                    {log.contactName ? `${log.contactName} · ` : ''}{log.contactPhone}
                  </Typography>
                  {log.templateName && (
                    <Typography variant="caption" color="text.secondary">
                      template: {log.templateName}
                    </Typography>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block">
                  {log.sentAt && `sent ${fmtTime(log.sentAt)}`}
                  {log.deliveredAt && ` · delivered ${fmtTime(log.deliveredAt)}`}
                  {log.failedAt && ` · failed ${fmtTime(log.failedAt)}`}
                  {log.attempts && log.attempts > 1 ? ` · ${log.attempts} attempts` : ''}
                </Typography>
                {(log.error || log.providerError) && (
                  <Typography variant="caption" color="error.main" display="block">
                    {log.providerError?.title || log.error}
                    {log.providerError?.code != null && ` (code ${log.providerError.code})`}
                    {log.providerError?.message && log.providerError.message !== log.providerError.title
                      ? ` — ${log.providerError.message}`
                      : ''}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}

// ─── Conversation-flow runs (message.received workflows) ────────────────────
// A completely separate render path from the lead-shaped table above — a
// conversation-flow run has no lead, no delay/campaign steps, no resumability.
// See ConversationFlowRun.model.ts / conversation-flow-run-log.service.ts on
// the backend for why this is a deliberately smaller, different shape.

/** One line describing what the flow did, in order — e.g.
 *  "Response sent → Handoff to team". */
function summarizeActions(actions: ConversationFlowRunActionSummary[]): string {
  if (!actions.length) return '—';
  return actions
    .map((a) => {
      switch (a.type) {
        case 'human_handoff':
          return 'Handoff to team';
        case 'template_reply':
          return `Template sent (${a.templateName ?? 'unknown'})`;
        case 'send_media':
          return 'File sent';
        case 'send_list_message':
          return `List message sent (${a.listButtonText ?? 'menu'})`;
        case 'send_button_message':
          return `Button message sent (${a.buttonCount ?? 0} button${a.buttonCount === 1 ? '' : 's'})`;
        case 'canned_reply':
        default:
          return 'Response sent';
      }
    })
    .join(' → ');
}

/** Only this panel paginates on the page, so it owns `?page=` outright. */
const FLOW_RUNS_URL_SPEC = { page: field.pageIndex() };

function ConversationFlowRunsPanel({ workflowId }: { workflowId: string }) {
  const navigate = useNavigate();
  const [runs, setRuns] = useState<ConversationFlowRun[]>([]);
  const { state: query, patch: patchQuery } = useUrlListState(FLOW_RUNS_URL_SPEC);
  const page = query.page; // 0-indexed for TablePagination
  const setPage = (value: number) => patchQuery({ page: value });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, meta } = await adminApi.getConversationFlowRunsPage(
        workflowId,
        page + 1,
        PAGE_SIZE
      );
      setRuns(data);
      setTotal(meta.total);
    } catch (err: any) {
      setError(err.message ?? 'Failed to load runs');
    } finally {
      setLoading(false);
    }
  }, [workflowId, page]);

  useEffect(() => { load(); }, [load]);

  const openInChatLogs = (conversationId: string) => {
    navigate(`/admin/activity/chat-logs?conversationId=${conversationId}`);
  };

  return (
    <Card>
      <CardContent sx={{ p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : runs.length === 0 ? (
          <Box textAlign="center" py={6}>
            <Typography color="text.secondary">
              No runs yet. This flow hasn't matched any customer message so far.
            </Typography>
          </Box>
        ) : (
          <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Matched on</TableCell>
                  <TableCell>What happened</TableCell>
                  <TableCell>When</TableCell>
                  <TableCell align="right">Chat</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runs.map((run) => (
                  <TableRow
                    key={run._id}
                    hover
                    onClick={() => openInChatLogs(run.conversationId)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {run.customerName || run.customerPhone}
                      </Typography>
                      {run.customerName && (
                        <Typography variant="caption" color="text.secondary">
                          {run.customerPhone}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {run.matchedButtonId ? (
                        <Chip size="small" variant="outlined" label={`Button: ${run.matchedButtonId}`} />
                      ) : run.matchedPhrase ? (
                        <Chip size="small" variant="outlined" label={`"${run.matchedPhrase}"`} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{summarizeActions(run.actions)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {fmtTime(run.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Open this conversation in Chat Logs">
                        <IconButton size="small" onClick={() => openInChatLogs(run.conversationId)}>
                          <ChevronRight fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
          />
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function WorkflowExecutions() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [workflow,   setWorkflow]   = useState<Workflow | null>(null);
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [execPage,   setExecPage]   = useState(0); // 0-indexed for TablePagination
  const [execTotal,  setExecTotal]  = useState(0);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);

  // Drill-down: 'list' (execution history) → 'detail' (a single run's steps).
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selected, setSelected] = useState<Execution | null>(null);
  const [campaignActivity, setCampaignActivity] = useState<CampaignActivity[]>([]);
  const [activityLoading,  setActivityLoading]  = useState(false);

  // ── Load ──────────────────────────────────────────────────────────────────

  const loadWorkflow = useCallback(async () => {
    if (!id) return;
    try {
      const wf = await adminApi.getWorkflowById(id);
      setWorkflow(wf);
    } catch {
      // header falls back to a generic title
    }
  }, [id]);

  const loadExecutions = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const { data, meta } = await adminApi.getWorkflowExecutionsPage({
        workflowId: id,
        page: execPage + 1,
        limit: PAGE_SIZE,
      });
      setExecutions(data);
      setExecTotal(meta.total);
    } catch (err: any) {
      setError(err.message ?? 'Failed to load executions');
    } finally {
      setLoading(false);
    }
  }, [id, execPage]);

  useEffect(() => { loadWorkflow(); }, [loadWorkflow]);
  useEffect(() => { loadExecutions(); }, [loadExecutions]);

  // ── Drill-down ──────────────────────────────────────────────────────────────

  const openExecution = (exec: Execution) => {
    setSelected(exec);
    setView('detail');
    setCampaignActivity([]);
    const hasSendCampaign = exec.executionLog?.some((e) => e.nodeType === 'send_campaign');
    if (hasSendCampaign) {
      setActivityLoading(true);
      adminApi
        .getExecutionCampaignActivity(exec._id)
        .then((data) => setCampaignActivity(Array.isArray(data) ? data : []))
        .catch(() => setCampaignActivity([]))
        .finally(() => setActivityLoading(false));
    }
  };

  const backToList = () => { setView('list'); setSelected(null); };

  // The header's Refresh button reloads whatever is actually on screen — the list
  // in 'list' view, or this one execution's live status/steps in 'detail' view.
  // Re-running loadExecutions alone (the old behavior) only refetched the
  // background list; `selected` — the detail view actually being looked at —
  // never updated, so a still-running/waiting execution looked frozen forever.
  const refreshCurrentView = () => {
    if (view === 'detail' && selected) {
      setLoading(true);
      adminApi
        .getWorkflowExecutionById(selected._id)
        .then((exec) => {
          setSelected(exec);
          const hasSendCampaign = exec.executionLog?.some(
            (e: ExecutionLogEntry) => e.nodeType === 'send_campaign'
          );
          if (hasSendCampaign) {
            setActivityLoading(true);
            adminApi
              .getExecutionCampaignActivity(exec._id)
              .then((data) => setCampaignActivity(Array.isArray(data) ? data : []))
              .catch(() => setCampaignActivity([]))
              .finally(() => setActivityLoading(false));
          }
        })
        .catch((err: any) => setError(err.message ?? 'Failed to refresh execution'))
        .finally(() => setLoading(false));
    } else {
      loadExecutions();
    }
  };

  const triggerLabel = workflow?.trigger?.type
    ? (TRIGGER_LABELS[workflow.trigger.type] ?? workflow.trigger.type)
    : '—';

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton onClick={() => navigate('/admin/automation/workflows')} size="small">
            <ArrowBack fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {workflow?.name || 'Workflow'}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              {workflow?.description && (
                <Typography variant="body2" color="text.secondary">
                  {workflow.description}
                </Typography>
              )}
              <Chip size="small" variant="outlined" label={`Trigger: ${triggerLabel}`} />
            </Stack>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Edit fontSize="small" />}
            onClick={() => navigate(`/admin/automation/workflows/${id}`)}
          >
            Edit workflow
          </Button>
          {workflow?.trigger?.type !== 'message.received' && (
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={refreshCurrentView} disabled={loading}>
                  <Refresh />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* message.received flows have no lead/campaign concept — a completely
          separate, smaller run history renders instead of everything below. */}
      {workflow?.trigger?.type === 'message.received' ? (
        id && <ConversationFlowRunsPanel workflowId={id} />
      ) : (
        <>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Card>
        <CardContent sx={{ p: 0 }}>
          {/* ── Execution history (default) ──────────────────────────────────── */}
          {view === 'list' && (
            loading ? (
              <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
            ) : executions.length === 0 ? (
              <Box textAlign="center" py={6}>
                <Typography color="text.secondary">
                  No executions yet. This workflow hasn’t run for any lead so far.
                </Typography>
              </Box>
            ) : (
              <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>Triggered by lead</TableCell>
                      <TableCell>Started</TableCell>
                      <TableCell>Completed</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell align="right">Steps</TableCell>
                      <TableCell align="right">Retries</TableCell>
                      <TableCell align="right">Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {executions.map((ex) => {
                      const lead = leadLabel(ex.context?.lead);
                      const endedAt = ex.completedAt || ex.failedAt;
                      return (
                        <TableRow key={ex._id} hover onClick={() => openExecution(ex)} sx={{ cursor: 'pointer' }}>
                          <TableCell>
                            <StatusDot
                              color={EXEC_STATUS_COLOR[ex.status] ?? 'default'}
                              label={EXEC_STATUS_LABEL[ex.status] ?? ex.status}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{lead.primary}</Typography>
                            {lead.secondary && (
                              <Typography variant="caption" color="text.secondary">{lead.secondary}</Typography>
                            )}
                          </TableCell>
                          <TableCell>{fmtTime(ex.startedAt)}</TableCell>
                          <TableCell>{fmtTime(endedAt)}</TableCell>
                          <TableCell>{fmtDuration(ex.startedAt, endedAt)}</TableCell>
                          <TableCell align="right">{ex.executionLog?.length ?? 0}</TableCell>
                          <TableCell align="right">{ex.retryCount}</TableCell>
                          <TableCell align="right"><ChevronRight color="action" fontSize="small" /></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={execTotal}
                page={execPage}
                onPageChange={(_e, p) => setExecPage(p)}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
              />
              </>
            )
          )}

          {/* ── Single execution detail ──────────────────────────────────────── */}
          {view === 'detail' && selected && (
            <Box>
              {/* Breadcrumb + run summary */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Button size="small" startIcon={<ArrowBack fontSize="small" />} onClick={backToList} sx={{ mb: 1.5 }}>
                  Back to execution history
                </Button>

                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
                    <StatusDot
                      color={EXEC_STATUS_COLOR[selected.status] ?? 'default'}
                      label={EXEC_STATUS_LABEL[selected.status] ?? selected.status}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Triggered by lead</Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Person fontSize="small" color="action" />
                      <Typography variant="body2">
                        {leadLabel(selected.context?.lead).primary}
                        {leadLabel(selected.context?.lead).secondary
                          ? ` · ${leadLabel(selected.context?.lead).secondary}`
                          : ''}
                      </Typography>
                    </Stack>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Started</Typography>
                    <Typography variant="body2">{fmtTime(selected.startedAt)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Completed</Typography>
                    <Typography variant="body2">{fmtTime(selected.completedAt || selected.failedAt)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Duration</Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <AccessTime fontSize="small" color="action" />
                      <Typography variant="body2">
                        {fmtDuration(selected.startedAt, selected.completedAt || selected.failedAt)}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </Box>

              {/* Step-by-step timeline */}
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
                  Steps
                </Typography>

                {selected.executionLog.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No step logs recorded.</Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {selected.executionLog.map((entry, i) => {
                      const isSendCampaign = entry.nodeType === 'send_campaign';
                      const isDelay = entry.nodeType === 'delay';
                      const nextEntry = selected.executionLog[i + 1];
                      const activities = isSendCampaign
                        ? campaignActivity.filter((a) => a.nodeId === entry.nodeId || !a.nodeId)
                        : [];
                      const delayPhrase = isDelay
                        ? formatDelay(entry.output?.amount, entry.output?.unit)
                        : null;
                      const detail = isSendCampaign
                        ? null
                        : isDelay
                          ? (delayPhrase ? `Waited ${delayPhrase}` : null)
                          : friendlyOutput(entry);
                      return (
                        <Box
                          key={i}
                          sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography
                              variant="caption"
                              sx={{ color: 'text.disabled', fontWeight: 700, minWidth: 20 }}
                            >
                              {i + 1}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {NODE_LABELS[entry.nodeType] ?? entry.nodeType}
                            </Typography>
                            <StatusDot
                              color={STEP_STATUS_COLOR[entry.status] ?? 'default'}
                              label={entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                            />
                            <Box flexGrow={1} />
                            <Typography variant="caption" color="text.secondary">
                              {stepDuration(entry, nextEntry)}
                            </Typography>
                          </Stack>


                          {entry.error && (
                            <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
                              {entry.error}
                            </Typography>
                          )}

                          {detail && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              {detail}
                            </Typography>
                          )}

                          {isSendCampaign && (
                            activityLoading ? (
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                                <CircularProgress size={14} />
                                <Typography variant="caption" color="text.secondary">
                                  Loading delivery details…
                                </Typography>
                              </Stack>
                            ) : activities.length === 0 ? (
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                Queued — no delivery record linked yet (sending is asynchronous).
                              </Typography>
                            ) : (
                              activities.map((act) => (
                                <CampaignDeliveryPanel
                                  key={act.runId}
                                  act={act}
                                  onOpenCampaign={(campaignId) => navigate(`/admin/campaigns/${campaignId}`)}
                                />
                              ))
                            )
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
        </>
      )}
    </Box>
  );
}
